import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contactsService } from '../services/contacts.service';
import { authService } from '../services/auth.service';
import {
  ContactRequest,
  ContactAction,
  CONTACT_STATUS_LABELS,
  CONTACT_ACTION_OPTIONS,
} from '../types/contact';
import { getErrorMessage } from '../utils/error';
import { formatDate } from '../utils/format';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';

/**
 * Espace agence — le détail d'un contact qui lui a été confié.
 *
 * PAGE et non panneau, comme les autres écrans de détail. L'agence y trouve le
 * contact, le contexte que le propriétaire lui a adressé, de quoi consigner un
 * échange, et l'historique. Rien d'autre : elle ne voit ni les autres demandes
 * de la colocation, ni le dossier locatif.
 */
const AgencyContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser()?.user;
  const authorName = currentUser
    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
    : '';

  const [contact, setContact] = useState<ContactRequest | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actions, setActions] = useState<ContactAction[]>([]);
  const [actionType, setActionType] = useState('CALLED');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // `GET /api/agency/contacts` ne renvoie déjà que ce qui est assigné à
      // cette agence : le périmètre est borné côté backend, la liste est courte.
      const liste = await contactsService.listAgencyContacts();
      const trouve = liste.find((c) => c.id === id) ?? null;
      setContact(trouve);
      setIntrouvable(trouve === null);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement du contact'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadActions = useCallback(async () => {
    if (!id) return;
    try {
      setActions(await contactsService.getActions(id));
    } catch {
      setActions([]);
    }
  }, [id]);

  useEffect(() => {
    loadData();
    loadActions();
  }, [loadData, loadActions]);

  const handleLog = async () => {
    if (!id) return;
    setBusy(true);
    setNotice(null);
    try {
      await contactsService.logAction(id, actionType, note);
      setNote('');
      await Promise.all([loadActions(), loadData()]);
      setNotice('Action enregistrée. Le propriétaire est notifié.');
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'enregistrement de l'action"));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await contactsService.close(id);
      setNotice('Contact clôturé.');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la clôture'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  if (introuvable || !contact) {
    return (
      <div className="candidats-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">Contact introuvable</p>
          <p className="socle-vide-aide">
            Il ne vous est plus assigné, ou votre accès à cette colocation a été
            suspendu.
          </p>
          <button
            className="btn-action btn-secondary"
            onClick={() => navigate('/agency/contacts')}
          >
            Retour aux contacts
          </button>
        </div>
      </div>
    );
  }

  const isClosed = contact.status === 'CLOSED';

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate('/agency/contacts')}
          >
            ← Contacts assignés
          </button>
          <h2>{contact.name}</h2>
          <p className="access-hint" style={{ margin: 0 }}>
            {CONTACT_STATUS_LABELS[contact.status] || contact.status}
          </p>
        </div>
      </div>

      {error && <div className="candidats-error">{error}</div>}
      {notice && <div className="candidats-notice">{notice}</div>}

      <div className="cx-detail">
        <div className="cx-card">
          <h4 className="cx-title">Contact</h4>
          <div className="cx-row">
            <span className="cx-label">Email</span>
            <span className="cx-value">{contact.email}</span>
          </div>
          <div className="cx-row">
            <span className="cx-label">Téléphone</span>
            <span className="cx-value">{contact.phone || '—'}</span>
          </div>
          {contact.message && <p className="cx-message">{contact.message}</p>}
          {contact.ownerContextMessage && (
            <div className="cx-context">
              Contexte du propriétaire : {contact.ownerContextMessage}
            </div>
          )}
        </div>

        {!isClosed && (
          <div className="cx-card">
            <h4 className="cx-title">Enregistrer une action</h4>
            <p className="action-author">
              Par&nbsp;
              <strong>{authorName || 'vous'}</strong>
              {currentUser?.email ? ` · ${currentUser.email}` : ''}
            </p>
            <div className="action-form-grid">
              <div className="action-field">
                <label htmlFor="action-type">Type d'action</label>
                <select
                  id="action-type"
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                >
                  {CONTACT_ACTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="action-field">
                <label htmlFor="action-note">Note</label>
                <textarea
                  id="action-note"
                  rows={5}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Détails de l'échange, prochaines étapes…"
                />
              </div>
              <div className="action-actions">
                <button className="btn-action btn-save" disabled={busy} onClick={handleLog}>
                  Enregistrer l'action
                </button>
                <button className="btn-action btn-secondary" disabled={busy} onClick={handleClose}>
                  Clôturer le contact
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="cx-card">
          <h4 className="cx-title">Historique</h4>
          {actions.length === 0 ? (
            <p className="timeline-empty">Aucune action pour l'instant.</p>
          ) : (
            <ul className="action-timeline">
              {actions.map((a) => (
                <li key={a.id} className="timeline-item">
                  <div className="timeline-action">{a.actionLabel}</div>
                  <div className="timeline-meta">
                    {a.agencyName || 'Agence'}
                    {a.agencyEmail ? ` · ${a.agencyEmail}` : ''}
                    {a.createdAt ? ` · ${formatDate(a.createdAt)}` : ''}
                  </div>
                  {a.note && <p className="timeline-note">{a.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyContactDetailPage;
